import { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_HEADING, FAQ_ITEMS } from "@/data/content";

/**
 * Shared FAQ accordion. Defaults to homepage FAQ data; pass `heading` and
 * `items` for page-specific FAQs. `injectSchema` adds FAQPage JSON-LD to <head>
 * (homepage injects its own schema separately, so it stays false there).
 */
export const FAQSection = ({ heading = FAQ_HEADING, items = FAQ_ITEMS, injectSchema = false }) => {
  useEffect(() => {
    if (!injectSchema) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((i) => ({
        "@type": "Question",
        name: i.q,
        acceptedAnswer: { "@type": "Answer", text: i.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [items, injectSchema]);

  return (
    <section data-testid="faq-section" className="bg-white py-16 md:py-22">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <p className="text-center font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
          FAQ
        </p>
        <h3 className="mt-3 text-center font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">
          {heading}
        </h3>

        <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
          {items.map((item, idx) => (
            <AccordionItem key={item.q} value={`faq-${idx}`} className="border-[#ececec]">
              <AccordionTrigger
                data-testid={`faq-item-trigger-${idx + 1}`}
                className="text-left font-nunito text-[15px] font-bold text-[#383838] hover:text-[#ee2e24] hover:no-underline py-5"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-item-content-${idx + 1}`}
                className="text-sm text-[#6e6e6e] leading-relaxed pb-5"
              >
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
